import { useState } from 'react';
import { ChevronDown, Pencil } from 'assets/icons';
import { fillText } from 'components/invoiceImport/invoiceImportText';
import Tooltip from 'components/tooltips/Tooltips';
import UseContext from 'hooks/useContext';

/**
 * The card the company panel and the mission detail are both built from.
 *
 * Four cards of the overview, the contracts and the amendments draw the same
 * frame: a title, something to fold it with, and a bordered box of content.
 * They had drawn it separately, which is how the contracts ended up with rows
 * that carried no border and buttons that matched nothing else.
 *
 * What differs between them is what sits under the content, so that is a slot.
 *
 * Drawn as the prototype's sections: one border, the header (title, actions,
 * the chevron that folds it), then the content under a line.
 */
// The title of a section in the prototype (« Informations de facturation »):
// 14 px, medium, whatever the size of the panel the card sits in.
export const SECTION_TITLE_CLASS = 'text-sm font-medium leading-5';

// A table in a card (« Coefficients de facturation », the notifications of a
// member): its grey head line, then its rows, each under a line. The caller
// gives the grid of its columns.
export const PANEL_TABLE_HEAD_CLASS = 'bg-[#fcfcfc] py-2.5 text-xs font-medium uppercase text-gray-500';
export const PANEL_TABLE_ROW_CLASS = 'border-t border-gray-100 py-3';

// The look of the SECONDARY button (components/button) beside it, « Inviter »
// for one: a light border, white, grey under the pointer, pressed on a click,
// and its height: 38 px, its 36 px and its border.
export const cardIconButtonClass =
  'border border-gray-100 bg-white rounded-md w-[38px] h-[38px] flex justify-center items-center cursor-pointer hover:bg-gray-100 active:bg-gray-200 motion-safe:enabled:active:scale-[.97] transition-[background-color,transform] duration-150 flex-shrink-0';

const PanelCard = ({
  /** Shown in a rounded square before the title. */
  icon,
  /** The square's background — a status colour on the amendment requests. */
  iconClassName = 'bg-gray-100',
  /** `icon` brings its own square (ListIcon): drawn as it is. */
  plainIcon = false,
  title,
  /** The design gives a card carrying an icon a bold title; a plain one else. */
  titleClassName = '',
  subtitle,
  /** Anything to show at the right of the header, before the fold control. */
  badge,
  onEdit,
  collapsible = false,
  defaultCollapsed = false,
  /**
   * The overview cards fold from an outlined square, next to the pencil that
   * matches it. The contract cards, which have no pencil, fold from a bare
   * chevron — the design draws no box around it.
   */
  outlinedFoldControl = true,
  /** Extra classes on the content box — a grid, mostly. */
  contentClassName = '',
  /** Under the content: the actions, which are what these cards differ by. */
  footer,
  children,
}) => {
  const [isCollapsed, setIsCollapsed] = useState(defaultCollapsed);
  const toggle = () => setIsCollapsed((previous) => !previous);
  const { language } = UseContext();
  // Many tests draw these cards without the app's context.
  const text = (key, fallback) =>
    fillText(language?.getValue(`panelCard.${key}`, fallback) ?? fallback, {
      title,
    });
  const editLabel = text('edit', 'Modifier {title}');
  const foldLabel = isCollapsed
    ? text('unfold', 'Déplier {title}')
    : text('fold', 'Replier {title}');

  // The pencil and the fold control sit on a header that now folds the card
  // itself: without this the click would run twice and land back where it
  // started.
  const withoutBubbling = (action) => (event) => {
    event.stopPropagation();
    action();
  };

  // A card without anything in its header (a table under a tab that already
  // names it) draws its content only.
  const hasHeader = Boolean(title || subtitle || icon || badge || onEdit || collapsible);

  return (
    <div className={'rounded-xl border border-gray-100'}>
      {hasHeader && (
      <div
        {...(collapsible && {
          role: 'button',
          tabIndex: 0,
          onClick: toggle,
          onKeyDown: (event) => {
            if (event.key !== 'Enter' && event.key !== ' ') return;
            // Space would scroll the panel the card sits in.
            event.preventDefault();
            toggle();
          },
        })}
        className={`flex items-center justify-between gap-2 px-4 py-3 ${
          isCollapsed ? 'rounded-xl' : 'rounded-t-xl'
        } ${collapsible ? 'cursor-pointer hover:bg-gray-50 transition-colors' : ''}`}
      >
        <div className={'flex items-center gap-3 min-w-0'}>
          {icon && !plainIcon && (
            <div
              className={`w-10 h-10 rounded-md flex items-center justify-center flex-shrink-0 ${iconClassName}`}
            >
              {icon}
            </div>
          )}
          {/* An icon that draws its own square (ListIcon). */}
          {icon && plainIcon && icon}
          <div className={'min-w-0'}>
            <div className={`truncate font-medium text-gray-900 ${titleClassName}`}>
              {title}
            </div>
            {subtitle && (
              <div className={'truncate text-sm text-gray-500'}>{subtitle}</div>
            )}
          </div>
        </div>
        <div className={'flex items-center gap-2 flex-shrink-0'}>
          {badge}
          {onEdit && (
            <Tooltip text={editLabel} placement={'top'}>
              <button
                type={'button'}
                aria-label={editLabel}
                className={cardIconButtonClass}
                onClick={withoutBubbling(onEdit)}
              >
                <Pencil wh={18} color={'#374151'} />
              </button>
            </Tooltip>
          )}
          {collapsible && (
            <Tooltip text={foldLabel} placement={'top'}>
              <button
                type={'button'}
                aria-label={foldLabel}
                className={
                  outlinedFoldControl
                    ? cardIconButtonClass
                    : 'flex justify-center items-center cursor-pointer flex-shrink-0'
                }
                onClick={withoutBubbling(toggle)}
              >
                <span className={`flex transition-transform ${isCollapsed ? '-rotate-90' : ''}`}>
                  <ChevronDown wh={18} color={'#374151'} />
                </span>
              </button>
            </Tooltip>
          )}
        </div>
      </div>
      )}
      {!isCollapsed && (
        <>
          {children && (
            <div
              className={`overflow-hidden ${hasHeader ? 'border-t border-gray-100' : 'rounded-t-xl'} ${
                footer ? '' : 'rounded-b-xl'
              } ${contentClassName}`}
            >
              {children}
            </div>
          )}
          {footer && <div className={'border-t border-gray-100 px-3 py-2'}>{footer}</div>}
        </>
      )}
    </div>
  );
};

export default PanelCard;
